import axios from "axios";
import { attachAuthRefresh } from "../utils/tokenRefresh";

// Base URL points to your Django backend's kundli app.
// e.g. https://jyotishconnect-production.up.railway.app/api/kundli
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

export const kundliApi = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
});

// ---------------------------------------------------------------------------
// Auth: every kundli endpoint requires IsAuthenticated (JWT Bearer token).
// The token must already exist in localStorage under 'kc_access_token' —
// it's set by your login/OTP-verify flow (users app), which is outside the
// scope of these 5 kundli APIs.
// ---------------------------------------------------------------------------
kundliApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("kc_access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// If the access token has expired, silently refresh it (using the refresh
// token from login) and retry the request once — see utils/tokenRefresh.js.
attachAuthRefresh(kundliApi);

// Unwraps the project's standard { success, message, data } / { success, message, errors } shape.
const unwrap = (promise) =>
  promise
    .then((res) => res.data)
    .catch((err) => {
      const body = err.response?.data;
      const message = body?.message || "Something went wrong.";
      const errors = body?.errors;
      throw { message, errors, status: err.response?.status };
    });

// ── 1. POST /api/kundli/match/ ───────────────────────────────────────────────
// personA / personB: pass EITHER { id: <existing profile id> } OR full inline
// birth-detail fields (name, gender, dob, tob, place, timezone).
export const createMatch = (personA, personB) => {
  const payload = {};
  if (personA.id) payload.person_a_id = personA.id;
  else payload.person_a = personA;

  if (personB.id) payload.person_b_id = personB.id;
  else payload.person_b = personB;

  return unwrap(kundliApi.post("/match/", payload));
};

// ── 5. POST /api/kundli/chart/ ───────────────────────────────────────────────
// pass EITHER { id: <existing profile id> } OR full inline birth-detail fields.
export const createChart = (profile) => {
  const payload = profile.id ? { profile_id: profile.id } : { profile };
  return unwrap(kundliApi.post("/chart/", payload));
};

// ── 6. GET /api/kundli/pricing/ ──────────────────────────────────────────────
// Server-owned prices for premium actions, e.g. { match: 99, chart: 49 }.
// Never hardcode these on the client — always read from here.
export const getPricing = () => unwrap(kundliApi.get("/pricing/"));
